// @ts-check

// Shared, glob-free boot pieces. Both entry implementations
// (framework/entry/eager-boot.js and framework/entry/lazy-boot.js) import from
// here; this module must never import the catalog globs (framework/eager.js,
// framework/loader.js) — the ESLint no-restricted-imports rule enforces the
// eager half, and the ce-lazy-purity build plugin fails a lazy production
// build whose entry graph picks up an implementation module.

/**
 * @typedef {import("vue").ComponentObjectPropsOptions} ComponentPropsOptions
 * @typedef {Record<string, string[]>} ComponentAliases
 * @typedef {{ global?: ComponentPropsOptions} & Record<string, ComponentPropsOptions>} SparkleElementsDefaults
 * @typedef {{ aliases?: ComponentAliases, defaults?: SparkleElementsDefaults}} SparkleElementsOptions
 */

/**
 * Marks the custom elements as initialized.
 *
 * Enables the custom elements to be visible and allows the slot content to be displayed.
 * It also indicates to the wider app that the custom elements have been loaded.
 *
 * The readiness event is a bare signal on purpose: a degraded reveal is
 * observable per instance (`data-sp-ce-failed` markers, CSS-selectable) and
 * operationally via the failure reports, so the event carries no detail.
 *
 * @see app/assets/stylesheets/custom_elements/setup.scss.erb
 * @see app/frontend/src/custom_elements/framework#registerCustomElement
 */
export function markAsInitialized() {
  // Set the visibility of the custom elements to visible.
  // This is required to prevent a flickering effect when the custom elements are loaded.
  document.documentElement.style.setProperty("--sp-ce-visibility", "visible");
  document.documentElement.style.setProperty("--sp-ce-slot-display", "unset");

  performance.mark("sp-ce:ready");

  // Indicates to the wider app that the custom elements have been loaded.
  Object.assign(window, { spElementsInitialized: true });
  window.dispatchEvent(new CustomEvent("sp:elements:initialized"));
}
